import styles from './NavBarDesktop.scss';

import cx from 'classnames';
import React, { FC, Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { NavigationItem } from 'src/api/contentful/contentTypes.cloverdotcom';
import { ButtonLinkV2 } from 'src/components/Button/ButtonLinkV2';
import { ContentLinkV2 } from 'src/components/ContentLinkV2/ContentLinkV2';
import { NavItemsList } from 'src/components/NavBarV2/NavItemsList/NavItemsList';
import { TextV2 } from 'src/components/TextV2/TextV2';
import { usePrevious } from 'src/hooks/UsePrevious';
import { useResizeObserver } from 'src/hooks/UseResizeObserver';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { componentKeyV2 } from 'src/utils/ComponentKeyV2';
import { stringFormatForTestAttr } from 'src/utils/StringFormatForTest';

import { CartLink } from '../CartLink/CartLink';
import { NavBarV2Interaction, logo } from '../NavBarV2';
import { NavDrawer, NavDrawerEffect } from '../NavDrawer/NavDrawer';
import { INITIAL_UI_STATE, UiController } from '../UseUiController';

export interface NavBarDesktopProps extends UiController {
  buttonUtilItems: Array<FlatEntry<NavigationItem>>;
  menuItems: Array<FlatEntry<NavigationItem>>;
  textUtilItems: Array<FlatEntry<NavigationItem>>;
  materialIconUtilItems: Array<FlatEntry<NavigationItem>>;
  expandInteraction: NavBarV2Interaction;
}

export const NavBarDesktop: FC<NavBarDesktopProps> = ({
  activeMenuIndex,
  buttonUtilItems,
  menuItems,
  textUtilItems,
  materialIconUtilItems,
  isDrawerOpen,
  closeDrawer,
  openSubNavDrawer,
  expandInteraction,
}) => {
  const { t } = useTranslation();

  const navBarMenuLeftRef = useRef<HTMLElement>(null);
  const prevActiveMenuIndex = usePrevious(activeMenuIndex);
  const isDrawerStart = activeMenuIndex !== INITIAL_UI_STATE.activeMenuIndex && prevActiveMenuIndex === INITIAL_UI_STATE.activeMenuIndex;

  useEffect(() => {
    const onBackButtonClick = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      closeDrawer();
    };

    window.addEventListener('pageshow', onBackButtonClick);

    return () => window.removeEventListener('pageshow', onBackButtonClick);
  }, []);

  // Observe left nav width to set --left-menu-width CSS variable
  useResizeObserver({
    targetRef: navBarMenuLeftRef, callback: ({ width }) => {
      if (navBarMenuLeftRef.current) navBarMenuLeftRef.current.style.setProperty('--left-menu-width', `${width}px`);
    },
  });

  /**
   * Handle the interaction for displaying sub nav menu
   *
   * @param {number} menuIndex The current index of the menu that is being interacted with
   */
  const handleInteraction = (event: NavBarV2Interaction, menuIndex: number) => {
    const isActive = menuIndex === activeMenuIndex;
    if (isActive && event === 'click') {
      closeDrawer();
    } else if (!isActive) {
      openSubNavDrawer(menuIndex);
    }
  };

  return (
    <div className={styles.navBarDesktopMenu}>
      <nav className={styles.navBarDesktopMenuLeft} ref={navBarMenuLeftRef} aria-label={t('navigation-header.left-menu')}>
        {logo}
        {menuItems.length > 0 && (
          <div className={cx(styles.navBarDesktopMenuItems, styles.navBarDesktopMenuStretch)} data-test="primary-nav-container">
            {menuItems.map((item, index) => {
              const title = item?.title;
              if (!title) return null;
              const isActive = index === activeMenuIndex;
              return (
                <Fragment key={componentKeyV2(item, index)}>
                  <TextV2
                    onClick={() => handleInteraction('click', index)}
                    onMouseOver={expandInteraction === 'hover' ? () => handleInteraction('hover', index) : undefined}
                    data-test={`${stringFormatForTestAttr(title)}`}
                    className={cx(styles.navBarDesktopTextButton, isActive && styles.navBarDesktopTextButtonActive)}
                    value={title}
                    variant="nav"
                    tagName="button"
                  />
                  <div
                    className={cx(styles.navBarDesktopMenuDrawer, styles.navBarDesktopContentDrawer)}
                    aria-expanded={isActive || undefined}
                  >
                    <NavDrawer
                      activeIndex={activeMenuIndex}
                      isOpen={isActive}
                      onClickCloseButton={closeDrawer}
                      effect={isDrawerStart ? NavDrawerEffect.SLIDE : NavDrawerEffect.FADE}
                      noBgColor
                    >
                      <NavItemsList entry={item} closeDrawer={closeDrawer} />
                    </NavDrawer>
                  </div>
                </Fragment>
              );
            })}
          </div>
        )}
        <div className={cx(styles.navBarDesktopMenuDrawer)}>
          <NavDrawer isOpen={isDrawerOpen}><span /></NavDrawer>
        </div>
      </nav>
      <nav className={styles.navBarDesktopMenuRight} aria-label={t('navigation-header.right-menu')}>
        {[...materialIconUtilItems, ...textUtilItems].map((item, index) => (
          <ContentLinkV2
            key={componentKeyV2(item, index)}
            entry={item}
            variant="nav"
            className={cx(
              styles.navBarDesktopUtilMenu,
              styles.navBarDesktopMenuStretch,
              styles.navBarDesktopTextButton
            )}
          />
        ))}
        {buttonUtilItems.map((item, index) => (
          <ButtonLinkV2
            key={componentKeyV2(item, index)}
            entry={item}
            className={styles.navBarDesktopUtilMenu}
          />
        ))}
        <CartLink />
      </nav>
    </div>
  );
};

NavBarDesktop.displayName = 'NavBarDesktop';
